import React, { Component } from 'react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import SessionHelper from '../helpers/SessionHelper';
import StyleHelper from '../../shared/helpers/StyleHelper';
import TimetableHelper from '../helpers/TimetableHelper';

import SessionPopover from './SessionPopover';
import Tooltip from './Tooltip';

import './TimetableList.css';
import { isScrolledToBottom } from '../../shared/utils/scroll';

class TimetableList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      show: [],
      width: null,
      currentHour: DateTime.local().hour,
      popoverId: null,
    };
    this.resizeFunction = null;
    this.resizeTimer = null;
    this.hoursVisible = 3;
    this.labelWidth = 150;
    this.rowHeight = 100;
    this.areaScroll = false;
    this.labelHeight = this.rowHeight + 2;
  }

  showPopover(popoverId) {
    this.setState({
      popoverId,
    });
  }

  hidePopover() {
    this.setState({
      popoverId: null,
    });
  }

  setCurrentHour(hour) {
    this.setState({
      currentHour: hour,
    });
  }

  toggleMore(identifier) {
    const show = this.state.show.slice();
    const index = show.indexOf(identifier);
    if (index < 0) {
      show.push(identifier);
    } else {
      show.splice(index, 1);
    }
    this.setState({
      show,
    });
  }

  handleResize() {
    const node = this.node;
    if (node && typeof node.getBoundingClientRect === 'function') {
      this.setState({
        width: node.getBoundingClientRect().width,
      });
    }
  }

  componentDidMount() {
    this.resizeFunction = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 250);
    };
    window.addEventListener('resize', this.resizeFunction);
    this.handleResize();
    this.scrollFunction = () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.handleScroll();
      }, 100);
    };
    window.addEventListener('scroll', this.scrollFunction);
    this.attachAreaScroll();
    this.firstScrollHandle = setTimeout(() => {
      this.handleScroll();
    }, 500);
  }

  componentWillUnmount() {
    clearTimeout(this.resizeTimer);
    clearTimeout(this.firstScrollHandle);
    window.removeEventListener('resize', this.resizeFunction);
    clearTimeout(this.scrollTimer);
    window.removeEventListener('scroll', this.scrollFunction);
    const node = this.area;
    if (node) {
      node.removeEventListener('scroll', this.scrollFunction);
    }
  }

  attachAreaScroll() {
    if (!this.areaScroll) {
      const node = this.area;
      if (node) {
        node.addEventListener('scroll', this.scrollFunction);
        this.areaScroll = true;
      }
    }
  }

  isScrolledToBottom() {
    return isScrolledToBottom(this.area);
  }

  handleScroll() {
    // do nothing
    const isAtBottom = this.isScrolledToBottom();
    if (!isAtBottom && !this.state.tooltip) {
      this.setState({ tooltip: true });
    } else if (isAtBottom && this.state.tooltip) {
      this.setState({ tooltip: false });
    }
  }

  componentDidUpdate() {
    if (typeof this.props.didUpdate === 'function') {
      this.props.didUpdate();
    }
    this.attachAreaScroll();
    this.handleScroll();
  }

  createHourList(currentHour, startHour) {
    const hoursVisible = this.hoursVisible;
    const filter = this.props.filter || {};
    const isHourBranded = StyleHelper.applyCustomStyle(
      this.props.hourStyle,
      '.TimetableList .hourList li.selected.branded'
    )
      ? 'branded'
      : '';

    const hourList = [];
    let hourIndex = 0;
    let hourTime = DateTime.fromISO(this.props.date).set({
      hour: startHour,
      minute: 0,
    });
    let currentDay = hourTime.day;
    while (hourTime.day === currentDay) {
      const hour = hourTime.hour;
      if (SessionHelper.isHourInTimeOfDayFilter(hour, filter)) {
        let setHour = hour;
        if (24 - setHour < hoursVisible) {
          setHour = 24 - hoursVisible;
        }
        hourList.push(
          <li
            key={hourIndex}
            className={`${hour === currentHour ? 'selected' : ''} ${isHourBranded}`}
            onClick={() => this.setCurrentHour(setHour)}
          >
            {hour}
          </li>
        );
      }
      hourTime = hourTime.plus({ hours: 1 });
      hourIndex += 1;
    }

    return hourList;
  }

  createHeadingList(currentHour) {
    const hoursVisible = this.hoursVisible;
    const hourWidth = (this.state.width - this.labelWidth) / hoursVisible;
    const headingList = [];
    let eventIndex = 0;
    let headingTime = DateTime.fromISO(this.props.date).set({
      hour: currentHour,
      minute: 0,
    });
    for (let i = 0; i < hoursVisible; i += 1) {
      const gridStyle = {
        width: `${hourWidth}px`,
        left: `${i * hourWidth + this.labelWidth}px`,
      };
      headingList.push(
        <div key={eventIndex} className="timetable-time" style={gridStyle}>
          {headingTime.toLocaleString(DateTime.TIME_24_SIMPLE)}
        </div>
      );
      headingTime = headingTime.plus({ hours: 1 });
      eventIndex += 1;
    }
    return headingList;
  }

  render() {
    if (this.state.width !== null) {
      const sessions = this.props.sessions || [];
      const filter = this.props.filter || {};
      const timetableContainer = [];
      const timetableList = [];
      const initialStartHour = SessionHelper.getMinimumHour(sessions);
      const hoursVisible = this.hoursVisible;
      const hourWidth = (this.state.width - this.labelWidth) / hoursVisible;
      const popover = {
        session: null,
        subEvent: null,
      };

      let currentHour = this.state.currentHour;
      let maxHour = 23;

      const isBranded = StyleHelper.applyCustomStyle(
        this.props.customStyle,
        '.TimetableList .timetable-row .timetable-session.branded'
      )
        ? 'branded'
        : '';

      let startHour;

      if (SessionHelper.hasTimeOfDayFilter(filter)) {
        for (let i = initialStartHour; i < 24; i += 1) {
          if (SessionHelper.isHourInTimeOfDayFilter(i, filter)) {
            if (!startHour) {
              startHour = i;
            }
            maxHour = i;
          }
        }
      } else {
        startHour = initialStartHour;
      }

      if (currentHour < startHour || currentHour > maxHour) {
        currentHour = startHour;
      }

      const hourList = this.createHourList(currentHour, startHour);
      const headingList = this.createHeadingList(currentHour);

      timetableContainer.push(
        <ul key={0} className="hourList">
          {hourList}
        </ul>
      );

      timetableContainer.push(
        <div key={1} className="timetable-header">
          {headingList}
        </div>
      );

      const buckets = TimetableHelper.sort(
        this.props.isCategoryFilter,
        sessions,
        'activity',
        filter,
        this.props.activityList
      );
      let index = 2;
      let rowCount = 0;

      for (const bucketName in buckets) {
        if (Object.prototype.hasOwnProperty.call(buckets, bucketName)) {
          const labelStyle = {
            width: `${this.labelWidth + 2}px`,
            top: `${rowCount * this.labelHeight}px`,
            height: `${buckets[bucketName].length * this.labelHeight}px`,
          };
          timetableList.push(
            <div key={index} className="timetable-label" style={labelStyle}>
              {bucketName}
            </div>
          );
          index += 1;
          let eventIndex = 0;
          for (const row of buckets[bucketName]) {
            let sessionList = [];
            for (const eventSeries of row) {
              for (let i = 0; i < hoursVisible; i += 1) {
                const gridStyle = {
                  width: `${hourWidth + 2}px`,
                  left: `${(i + currentHour - startHour) * hourWidth}px`,
                };
                sessionList.push(
                  <div key={eventIndex} className="timetable-grid" style={gridStyle}></div>
                );
                eventIndex += 1;
              }
              for (const subEvent of eventSeries.subEvent) {
                const startTime = DateTime.fromISO(subEvent.startDate);
                let endTime = DateTime.fromISO(subEvent.endDate);
                if (endTime.second === 59) {
                  endTime = endTime.plus({ seconds: 1 });
                }
                const uniqueId = `${eventSeries.identifier}-${subEvent.startDate}-${index}`;
                let itemStartHour = startTime.hour;
                let itemStartMinute = startTime.minute;
                const itemEndHour = endTime.hour;

                if (
                  itemStartHour < currentHour &&
                  (itemEndHour > currentHour || (itemEndHour === currentHour && endTime.minute > 0))
                ) {
                  itemStartHour = currentHour;
                  itemStartMinute = 0;
                }

                const leftOffset = (itemStartHour - startHour + itemStartMinute / 60) * hourWidth;

                const width =
                  (itemEndHour - startHour + endTime.minute / 60) * hourWidth - leftOffset;

                const sessionStyle = {
                  width: `${width - 2}px`,
                  left: `${leftOffset + 2}px`,
                };

                sessionList.push(
                  <div
                    key={eventIndex}
                    title={eventSeries.name}
                    className={`timetable-session ${isBranded}`}
                    onClick={() => this.showPopover(uniqueId)}
                    style={sessionStyle}
                  >
                    <span className="sessionName">{eventSeries.name}</span>
                  </div>
                );

                if (uniqueId === this.state.popoverId) {
                  popover.session = eventSeries;
                  popover.subEvent = subEvent;
                }

                eventIndex += 1;
              }
            }
            const rowStyle = {
              width: `${(24 - startHour) * hourWidth}px`,
              left: `${(startHour - currentHour) * hourWidth + this.labelWidth}px`,
            };
            timetableList.push(
              <div key={index} className="timetable-row" style={rowStyle}>
                {sessionList}
              </div>
            );
            index += 1;
            rowCount += 1;
          }
        }
      }

      if (timetableList.length === 0) {
        this.areaScroll = false;
        return (
          <div ref={(node) => (this.node = node)} className="NoResults">
            <span>
              <p>There are no sessions available with the current filter sessions.</p>
              <p>Try adjusting the filters to see what else is available.</p>
            </span>
          </div>
        );
      }

      let sessionPopover;
      if (popover.session) {
        sessionPopover = (
          <SessionPopover
            session={popover.session}
            eventSeries={popover.session}
            subEvent={popover.subEvent}
            checkoutTheme={this.props.checkoutTheme}
            checkoutUrl={this.props.checkoutUrl}
            shouldShowExactAvailability={this.props.shouldShowExactAvailability}
            shouldShowNoAvailability={this.props.shouldShowNoAvailability}
            shouldNotShowDeepLinkButton={this.props.shouldNotShowDeepLinkButton}
            bookableProviders={this.props.bookableProviders}
            buttonStyle={this.props.buttonStyle}
            memberDeepLinks={this.props.memberDeepLinks}
            onHide={() => this.hidePopover()}
            showPrices={this.props.showPrices}
          />
        );
      }

      const ttStyle = {};
      if (this.props.height) {
        ttStyle.maxHeight = `${this.props.height - 15}rem`;
      }

      let tooltip;
      if (this.state.tooltip) {
        tooltip = <Tooltip title="Scroll down for more sessions" />;
      }

      return (
        <div ref={(node) => (this.node = node)}>
          {sessionPopover}
          <div className="TimetableList">
            {timetableContainer}
            <div ref={(area) => (this.area = area)} className="timetable-area" style={ttStyle}>
              {timetableList}
            </div>
          </div>
          {tooltip}
        </div>
      );
    }
    this.areaScroll = false;
    return <div ref={(node) => (this.node = node)} className="TimetableList"></div>;
  }
}

TimetableList.propTypes = {
  didUpdate: PropTypes.func,
  date: PropTypes.string,
  sessions: PropTypes.array,
  filter: PropTypes.object,
  customStyle: PropTypes.object,
  hourStyle: PropTypes.object,
  checkoutUrl: PropTypes.string,
  checkoutTheme: PropTypes.string,
  buttonStyle: PropTypes.object,
  height: PropTypes.number,
  shouldShowExactAvailability: PropTypes.bool,
  shouldShowNoAvailability: PropTypes.bool,
  shouldNotShowDeepLinkButton: PropTypes.bool,
  bookableProviders: PropTypes.array,
  memberDeepLinks: PropTypes.object,
  isCategoryFilter: PropTypes.bool,
  activityList: PropTypes.object,
  showPrices: PropTypes.bool,
};

export default TimetableList;
